import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Stack from '@mui/joy/Stack'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { LiveDatMetricClass, LiveDatUsageResponse } from '@numeo/types-core'
import { liveDatSearchApi } from 'api/liveDatSearch'

const WINDOWS = [7, 14, 30]
const DAY_MS = 24 * 60 * 60 * 1000

const CLASS_LABELS: Record<LiveDatMetricClass, string> = {
    new_user: 'New users, live DAT',
    new_user_control: 'New users, control (Smart Search)',
    platinum: 'Platinum',
    none: 'Everyone else',
}
const CLASS_ORDER: LiveDatMetricClass[] = ['new_user', 'new_user_control', 'platinum', 'none']

/** A Los Angeles calendar day, as the server buckets them. */
function laDay(date: Date): string {
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' })
}

function formatUses(value: number): string {
    return value.toLocaleString()
}

/**
 * How many pooled DAT token picks mobile searches made, by live-DAT class and, for new users, by active day (ADR-041).
 * The numbers come from the same rollup as the DAT Token Usage page, so they count token picks (a retry on a second
 * token counts again), not searches.
 */
export default function LiveDatUsageCard() {
    const [days, setDays] = useState(14)
    const [usage, setUsage] = useState<LiveDatUsageResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const now = Date.now()
        setUsage(null)
        setError(null)
        liveDatSearchApi
            .usage(laDay(new Date(now - (days - 1) * DAY_MS)), laDay(new Date(now)))
            .then((next) => !cancelled && setUsage(next))
            .catch(() => !cancelled && setError('Failed to load live DAT usage'))
        return () => {
            cancelled = true
        }
    }, [days])

    const { byClass, byActiveDay, total } = useMemo(() => {
        const byClassTotals = new Map<LiveDatMetricClass, number>()
        const byDay = new Map<number, number>()
        let sum = 0
        for (const row of usage?.rows ?? []) {
            sum += row.uses
            byClassTotals.set(row.liveClass, (byClassTotals.get(row.liveClass) ?? 0) + row.uses)
            if (row.liveClass === 'new_user' && row.activeDay !== 'none') byDay.set(Number(row.activeDay), (byDay.get(Number(row.activeDay)) ?? 0) + row.uses)
        }
        return { byClass: byClassTotals, byActiveDay: [...byDay.entries()].sort((a, b) => a[0] - b[0]), total: sum }
    }, [usage])

    return (
        <Card variant="outlined">
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <div>
                        <Typography level="title-md">DAT token usage by class</Typography>
                        <Typography level="body-sm">
                            Pooled DAT token picks by mobile searches. A pick is one live DAT request, so a retry counts again.
                        </Typography>
                    </div>
                    <Select size="sm" value={days} onChange={(_, value) => value && setDays(value)} sx={{ width: 140 }}>
                        {WINDOWS.map((option) => (
                            <Option key={option} value={option}>
                                Last {option} days
                            </Option>
                        ))}
                    </Select>
                </Stack>

                {error && (
                    <Alert color="danger" variant="soft">
                        {error}
                    </Alert>
                )}
                {!usage && !error && <CircularProgress size="sm" />}

                {usage && (
                    <>
                        <Table size="sm" aria-label="DAT token picks by class">
                            <thead>
                                <tr>
                                    <th>Class</th>
                                    <th style={{ textAlign: 'right' }}>Token picks</th>
                                    <th style={{ textAlign: 'right' }}>Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CLASS_ORDER.map((liveClass) => {
                                    const uses = byClass.get(liveClass) ?? 0
                                    return (
                                        <tr key={liveClass}>
                                            <td>{CLASS_LABELS[liveClass]}</td>
                                            <td style={{ textAlign: 'right' }}>{formatUses(uses)}</td>
                                            <td style={{ textAlign: 'right' }}>{total > 0 ? `${((uses / total) * 100).toFixed(1)}%` : '-'}</td>
                                        </tr>
                                    )
                                })}
                                <tr>
                                    <td>
                                        <b>Total</b>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <b>{formatUses(total)}</b>
                                    </td>
                                    <td />
                                </tr>
                            </tbody>
                        </Table>

                        <div>
                            <Typography level="title-sm">New users, live DAT, by active day</Typography>
                            <Typography level="body-xs" sx={{ mb: 1 }}>
                                How much of the allowance is really used. Control users have no active day, so they appear only in the table above.
                            </Typography>
                            {byActiveDay.length === 0 ? (
                                <Typography level="body-sm">No live DAT picks by new users in this window.</Typography>
                            ) : (
                                <Table size="sm" aria-label="Live DAT token picks by active day">
                                    <thead>
                                        <tr>
                                            <th>Active day</th>
                                            <th style={{ textAlign: 'right' }}>Token picks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byActiveDay.map(([day, uses]) => (
                                            <tr key={day}>
                                                <td>Day {day}</td>
                                                <td style={{ textAlign: 'right' }}>{formatUses(uses)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </>
                )}
            </Stack>
        </Card>
    )
}
